import React, { useState, useEffect } from 'react';
import { Search, RefreshCw, ChevronRight, ChevronLeft, UserCheck } from 'lucide-react';
import { toast } from 'react-hot-toast';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Loader from '../../components/ui/Loader';
import Badge from '../../components/ui/Badge';
import Select from '../../components/ui/Select';
import usuarioService from '../../services/usuarioService';

export default function AsignarPostulantesAdmin() {
  const [loading, setLoading] = useState(true);
  const [postulantesDisponibles, setPostulantesDisponibles] = useState([]);
  const [supervisores, setSupervisores] = useState([]);
  const [selectedSupervisor, setSelectedSupervisor] = useState('');
  const [searchDisponibles, setSearchDisponibles] = useState('');
  const [searchSupervisores, setSearchSupervisores] = useState('');
  const [selectedDisponibles, setSelectedDisponibles] = useState([]);
  const [selectedSupervisores, setSelectedSupervisores] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await usuarioService.getAllUsuarios();

      // Filtrar postulantes y supervisores
      const postulantes = data.filter(u => u.rol === 'POSTULANTE');
      const supervisoresList = data.filter(u => u.rol === 'SUPERVISOR');

      // Postulantes disponibles (sin supervisor asignado)
      const disponibles = postulantes.filter(p => !p.supervisorId);

      setPostulantesDisponibles(disponibles);
      setSupervisores(supervisoresList);
    } catch (error) {
      toast.error('Error al cargar datos');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAsignar = async () => {
    if (selectedDisponibles.length === 0) {
      toast.error('Seleccione al menos un postulante');
      return;
    }
    if (!selectedSupervisor) {
      toast.error('Seleccione un supervisor');
      return;
    }

    try {
      // Asignar supervisorId a cada postulante seleccionado
      const promises = selectedDisponibles.map(id => {
        const postulante = postulantesDisponibles.find(p => p.id === id);
        return usuarioService.updateUsuario(id, {
          ...postulante,
          supervisorId: parseInt(selectedSupervisor)
        });
      });

      await Promise.all(promises);
      toast.success(`${selectedDisponibles.length} postulante(s) asignado(s) al supervisor`);
      setSelectedDisponibles([]);
      loadData();
    } catch (error) {
      toast.error('Error al Movimiento');
      console.error(error);
    }
  };

  const handleDesasignar = async () => {
    if (selectedSupervisores.length === 0) {
      toast.error('Seleccione al menos un supervisor');
      return;
    }

    try {
      // Obtener todos los postulantes asignados a los supervisores seleccionados
      const postulantesAsignados = [];
      selectedSupervisores.forEach(supervisorId => {
        const supervisor = supervisores.find(s => s.id === parseInt(supervisorId));
        if (supervisor && supervisor.postulantes) {
          postulantesAsignados.push(...supervisor.postulantes);
        }
      });

      if (postulantesAsignados.length === 0) {
        toast.error('No hay postulantes asignados a los supervisores seleccionados');
        return;
      }

      // Remover supervisorId de cada postulante
      const promises = postulantesAsignados.map(postulante =>
        usuarioService.updateUsuario(postulante.id, {
          ...postulante,
          supervisorId: null
        })
      );

      await Promise.all(promises);
      toast.success(`${postulantesAsignados.length} postulante(s) desasignado(s)`);
      setSelectedSupervisores([]);
      loadData();
    } catch (error) {
      toast.error('Error al desMovimiento');
      console.error(error);
    }
  };

  const toggleSelectDisponible = (id) => {
    setSelectedDisponibles(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectSupervisor = (id) => {
    setSelectedSupervisores(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const selectAllDisponibles = () => {
    if (selectedDisponibles.length === filteredDisponibles.length) {
      setSelectedDisponibles([]);
    } else {
      setSelectedDisponibles(filteredDisponibles.map(p => p.id));
    }
  };

  const selectAllSupervisores = () => {
    if (selectedSupervisores.length === filteredSupervisores.length) {
      setSelectedSupervisores([]);
    } else {
      setSelectedSupervisores(filteredSupervisores.map(s => s.id.toString()));
    }
  };

  const filteredDisponibles = postulantesDisponibles.filter(p =>
    p.name?.toLowerCase().includes(searchDisponibles.toLowerCase()) ||
    p.lastname?.toLowerCase().includes(searchDisponibles.toLowerCase()) ||
    p.username?.toLowerCase().includes(searchDisponibles.toLowerCase()) ||
    p.dni?.includes(searchDisponibles) ||
    p.email?.toLowerCase().includes(searchDisponibles.toLowerCase())
  );

  const filteredSupervisores = supervisores.filter(s =>
    s.name?.toLowerCase().includes(searchSupervisores.toLowerCase()) ||
    s.lastname?.toLowerCase().includes(searchSupervisores.toLowerCase()) ||
    s.username?.toLowerCase().includes(searchSupervisores.toLowerCase()) ||
    s.email?.toLowerCase().includes(searchSupervisores.toLowerCase())
  );

  // Calcular postulantes asignados por supervisor
  const supervisoresConPostulantes = filteredSupervisores.map(supervisor => ({
    ...supervisor,
    postulantesAsignados: postulantesDisponibles.filter(p => p.supervisorId === supervisor.id).length
  }));

  if (loading) return <Loader />;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-neutral-800">Movimiento</h1>
          <p className="text-neutral-600 mt-1">Asigne postulantes a supervisores</p>
        </div>
        <Button variant="outline" onClick={loadData} className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Actualizar
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Postulantes Disponibles */}
        <div className="lg:col-span-1 bg-white rounded-lg shadow-sm border border-neutral-200">
          <div className="p-4 border-b border-neutral-200">
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-lg font-semibold text-neutral-800">
                Postulantes Disponibles
              </h2>
              <Badge variant="secondary">{filteredDisponibles.length}</Badge>
            </div>
            <Input
              placeholder="Buscar postulantes..."
              value={searchDisponibles}
              onChange={(e) => setSearchDisponibles(e.target.value)}
              icon={Search}
            />
            <div className="mt-2">
              <label className="flex items-center gap-2 text-sm text-neutral-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedDisponibles.length === filteredDisponibles.length && filteredDisponibles.length > 0}
                  onChange={selectAllDisponibles}
                  className="rounded border-neutral-300"
                />
                Seleccionar todos
              </label>
            </div>
          </div>
          <div className="overflow-y-auto max-h-96">
            {filteredDisponibles.length === 0 ? (
              <div className="p-8 text-center text-neutral-500">
                No hay postulantes disponibles
              </div>
            ) : (
              <div className="divide-y divide-neutral-200">
                {filteredDisponibles.map((postulante) => (
                  <label
                    key={postulante.id}
                    className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-neutral-50 transition-colors ${
                      selectedDisponibles.includes(postulante.id) ? 'bg-primary-50' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedDisponibles.includes(postulante.id)}
                      onChange={() => toggleSelectDisponible(postulante.id)}
                      className="rounded border-neutral-300"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-neutral-800 truncate">
                        {postulante.name} {postulante.lastname}
                      </p>
                      <p className="text-sm text-neutral-600 truncate">
                        {postulante.email}
                      </p>
                      <p className="text-xs text-neutral-500">
                        DNI: {postulante.dni}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Panel de Asignación */}
        <div className="lg:col-span-1 flex flex-col items-center justify-center gap-4">
          <div className="w-full max-w-xs space-y-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Seleccionar Supervisor
              </label>
              <Select
                value={selectedSupervisor}
                onChange={(e) => setSelectedSupervisor(e.target.value)}
                placeholder="Elegir supervisor..."
              >
                {supervisores.map((supervisor) => (
                  <option key={supervisor.id} value={supervisor.id}>
                    {supervisor.name} {supervisor.lastname}
                  </option>
                ))}
              </Select>
            </div>

            <Button
              onClick={handleAsignar}
              disabled={selectedDisponibles.length === 0 || !selectedSupervisor}
              className="gap-2 w-full"
            >
              <UserCheck className="w-4 h-4" />
              Movimiento
            </Button>

            <div className="border-t border-neutral-200 pt-4">
              <Button
                onClick={handleDesasignar}
                disabled={selectedSupervisores.length === 0}
                variant="outline"
                className="gap-2 w-full"
              >
                <ChevronLeft className="w-4 h-4" />
                Desasignar de Supervisores
              </Button>
            </div>
          </div>

          <div className="text-center text-sm text-neutral-600 mt-4">
            <p>Postulantes seleccionados: {selectedDisponibles.length}</p>
            <p>Supervisores seleccionados: {selectedSupervisores.length}</p>
          </div>
        </div>

        {/* Supervisores */}
        <div className="lg:col-span-1 bg-white rounded-lg shadow-sm border border-neutral-200">
          <div className="p-4 border-b border-neutral-200">
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-lg font-semibold text-neutral-800">
                Supervisores
              </h2>
              <Badge variant="success">{filteredSupervisores.length}</Badge>
            </div>
            <Input
              placeholder="Buscar supervisores..."
              value={searchSupervisores}
              onChange={(e) => setSearchSupervisores(e.target.value)}
              icon={Search}
            />
            <div className="mt-2">
              <label className="flex items-center gap-2 text-sm text-neutral-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedSupervisores.length === filteredSupervisores.length && filteredSupervisores.length > 0}
                  onChange={selectAllSupervisores}
                  className="rounded border-neutral-300"
                />
                Seleccionar todos
              </label>
            </div>
          </div>
          <div className="overflow-y-auto max-h-96">
            {filteredSupervisores.length === 0 ? (
              <div className="p-8 text-center text-neutral-500">
                No hay supervisores disponibles
              </div>
            ) : (
              <div className="divide-y divide-neutral-200">
                {supervisoresConPostulantes.map((supervisor) => (
                  <label
                    key={supervisor.id}
                    className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-neutral-50 transition-colors ${
                      selectedSupervisores.includes(supervisor.id.toString()) ? 'bg-green-50' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedSupervisores.includes(supervisor.id.toString())}
                      onChange={() => toggleSelectSupervisor(supervisor.id.toString())}
                      className="rounded border-neutral-300"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-neutral-800 truncate">
                        {supervisor.name} {supervisor.lastname}
                      </p>
                      <p className="text-sm text-neutral-600 truncate">
                        {supervisor.email}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="info" className="text-xs">
                          {supervisor.postulantesAsignados || 0} postulantes
                        </Badge>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}